"use client";

import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { CreateWorkingHourFormSchema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import Buttons from "@/components/core/Buttons";
import { WorkingHours } from "@/actions/types";
import hooks from "@/hooks";
import { TimePicker } from "@/components/ui/datetime-picker";
import { format, parse } from "date-fns";
import { Label } from "@/components/ui/label";
import { UserJWTDecoded } from "@/types";
import FormFields from "../Fields";
import { WeekDayEnum } from "@/enum";

interface ICreateWorkingHourForm {
  closeModal: (isOpen: boolean) => void;
  handleAfterCreateWorkingHour: (updatedWorkingHour: WorkingHours) => void;
  user: UserJWTDecoded["user"];
  availableWeekdaysToCreate: WeekDayEnum[];
}

type WorkingHour = z.infer<typeof CreateWorkingHourFormSchema>;

const CreateWorkingHourForm = ({
  closeModal,
  handleAfterCreateWorkingHour,
  user,
  availableWeekdaysToCreate,
}: ICreateWorkingHourForm) => {
  const { toast } = useToast();

  const form = useForm<WorkingHour>({
    resolver: zodResolver(CreateWorkingHourFormSchema),
    defaultValues: {
      week_day: "",
      open_at: "0000",
      close_at: "0000",
    },
  });

  const createWorkingHourService = hooks.useCreateSalonWorkingHour({
    onSuccess: (data) => {
      toast({
        title: "Horário de trabalho atualizado com sucesso",
        description: ``,
        variant: "success",
      });
      handleAfterCreateWorkingHour(data);
      closeModal(false);
    },
    onError: (error) => {
      toast({
        title: "Falha ao atualizar horário de trabalho",
        description: "Não foi possível atualizar o horário de trabalho",
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: WorkingHour) {
    await createWorkingHourService.mutateAsync({
      owner_id: user.userid,
      open_at: data.open_at,
      close_at: data.close_at,
      week_day: data.week_day,
    });
  }

  const openAt = form.watch().open_at;
  const closeAt = form.watch().close_at;

  const weekOptions = availableWeekdaysToCreate.map((weekday) => ({
    label: WeekDayEnum[weekday as unknown as keyof typeof WeekDayEnum],
    value: weekday,
  }));

  const weekDay = form.watch().week_day;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className=" w-full flex flex-col items-center gap-y-6"
      >
        <div className="w-full grid grid-cols-1 gap-4 items-center">
          <div className="w-full flex items-center justify-between gap-4">
            <FormFields.SelectFormField
              placeholder="Selecione o dia da semana"
              key={`week_day-${weekDay}`}
              label="Dia da semana"
              form={form}
              formName="week_day"
              options={weekOptions}
            />
          </div>

          <div className="w-full flex items-center justify-between gap-4">
            <Label>Abertura</Label>
            <TimePicker
              key={`open_at`}
              granularity="minute"
              date={parse(
                openAt.replace(/^(\d{2})(\d{2})$/, "$1:$2"),
                "HH:mm",
                new Date(),
              )}
              onChange={(date) => {
                if (date) {
                  const formattedTime = format(date, "HHmm");
                  form.setValue("open_at", formattedTime);
                }
              }}
            />
          </div>
          {form.formState.errors.open_at && (
            <h3>{form.formState.errors.open_at.message}</h3>
          )}

          <div className="w-full flex items-center justify-between gap-4">
            <Label>Fechamento</Label>
            <TimePicker
              key={`close_at`}
              granularity="minute"
              date={parse(
                closeAt.replace(/^(\d{2})(\d{2})$/, "$1:$2"),
                "HH:mm",
                new Date(),
              )}
              onChange={(date) => {
                if (date) {
                  const formattedTime = format(date, "HHmm");
                  form.setValue("close_at", formattedTime);
                }
              }}
            />
          </div>
        </div>

        <div className="w-full flex items-center gap-4 mt-4">
          <Buttons.DefaultButton
            title={"Confirmar"}
            type="button"
            onClick={() => {
              onSubmit(form.getValues());
            }}
            isLoading={createWorkingHourService.isPending}
          />
          <Buttons.DefaultButton
            variant="secondary"
            title={"Cancelar"}
            type="button"
            onClick={() => closeModal(false)}
          />
        </div>
      </form>
    </Form>
  );
};

export default CreateWorkingHourForm;
